import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { BrandWordmark } from './BrandWordmark';
import { HeaderProfile } from './HeaderProfile';
import { NotificationsBell } from './NotificationsBell';
import { HeaderSearch } from './search/HeaderSearch';
import { usePageHeading } from './usePageHeading';

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

type AppHeaderProps = {
  overflowOpen?: boolean;
  onOpenOverflow?: () => void;
};

/**
 * Top chrome — search · primary action · notifications · profile (all pages).
 */
export function AppHeader({ overflowOpen = false, onOpenOverflow }: AppHeaderProps) {
  const { t } = useTranslation('layout');
  const { pathname } = useLocation();
  const heading = usePageHeading(pathname);
  const isHome = pathname === '/';
  const primary = heading.primaryAction;

  return (
    <header
      className="sticky top-0 z-20 flex shrink-0 items-center gap-2 border-b px-3 py-3 sm:gap-4 sm:px-[clamp(18px,2.4vw,32px)] sm:py-3.5 lg:border-b-0"
      style={{
        background: 'var(--gs-panel)',
        borderColor: 'var(--gs-line)',
        color: 'var(--gs-ink)',
      }}
    >
      <button
        type="button"
        className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border-0 lg:hidden"
        style={{ color: 'var(--gs-ink)', background: 'var(--gs-panel2)' }}
        aria-label={t('nav.more')}
        aria-expanded={overflowOpen}
        onClick={onOpenOverflow}
      >
        <MoreIcon />
      </button>

      {isHome ? (
        <div className="flex min-w-0 flex-1 items-center lg:hidden">
          <BrandWordmark className="h-5 max-w-[140px]" />
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 lg:hidden">
          <h1
            className="m-0 truncate text-lg font-extrabold leading-none tracking-[-0.03em]"
            style={{ color: 'var(--gs-ink)' }}
          >
            {heading.title}
          </h1>
          {heading.subtitle ? (
            <span
              className="hidden truncate text-[13px] leading-none sm:block"
              style={{ color: 'var(--gs-muted)' }}
            >
              {heading.subtitle}
            </span>
          ) : null}
        </div>
      )}

      <HeaderSearch className="hidden max-w-[640px] flex-[1_1_260px] lg:block" />

      <div className="ms-auto flex shrink-0 items-center gap-2 sm:gap-2.5">
        {heading.secondaryAction ? (
          <Link
            to={heading.secondaryAction.to}
            className="hidden items-center gap-2 whitespace-nowrap rounded-xl border px-[15px] py-3 text-[14.5px] font-bold no-underline transition hover:border-[var(--gs-accent)] md:inline-flex"
            style={{
              color: 'var(--gs-ink)',
              background: 'var(--gs-panel2)',
              borderColor: 'var(--gs-line)',
            }}
          >
            <PlusIcon />
            {heading.secondaryAction.label}
          </Link>
        ) : null}

        <Link
          to={primary.to}
          className={[
            'items-center gap-[9px] whitespace-nowrap rounded-xl px-[18px] py-3 text-[14.5px] font-bold no-underline transition hover:opacity-90',
            isHome ? 'hidden lg:inline-flex' : 'inline-flex',
          ].join(' ')}
          style={{
            color: 'var(--gs-accent-ink)',
            background: 'var(--gs-accent-solid)',
          }}
          aria-label={primary.label}
        >
          <PlusIcon />
          <span className="hidden sm:inline">{primary.label}</span>
        </Link>

        <NotificationsBell />

        <HeaderProfile />
      </div>
    </header>
  );
}
